/**
 * SDCX 宏动作浏览器本地持久化。
 * 作为 macro_tab 的 UI 缓存：设备读写失败或未连接时回退；保存成功时与设备同步。
 */

import { SDCX_MACRO_DEFAULT_DELAY_MS } from "./sdcxMacro";
import { SDCX_MACRO_UI_SLOT_COUNT } from "./sdcxExtraKeys";

export type SdcxMacroIntervalMode = 0 | 1;

export const SDCX_MACRO_INTERVAL_STORAGE_KEY = "sdcxMacroIntervalPref";

type SdcxMacroIntervalPrefRecord = {
  intervalMode: SdcxMacroIntervalMode;
  fixedIntervalMs: number;
};

/** 与录制页 Action 结构一致，便于 JSON 序列化 */
export type SdcxMacroLocalAction = {
  actionType: number;
  status: number;
  delay: number;
  keyCode: number;
  keyName?: string;
};

export type SdcxMacroLocalSlotRecord = {
  actions: SdcxMacroLocalAction[];
  valid: boolean;
  /** 间隔模式：0 默认时间 / 1 固定时间 */
  mode: number;
  /** 固定间隔 ms */
  repNum: number;
  updatedAt: number;
};

export const SDCX_MACRO_LOCAL_STORAGE_KEY = "sdcxMacroSlotData";

function isDemoMode(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem("isDemoMode") === "true";
}

function readLocalMap(): Record<string, SdcxMacroLocalSlotRecord | null> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(SDCX_MACRO_LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeLocalMap(
  map: Record<string, SdcxMacroLocalSlotRecord | null>
): void {
  if (typeof localStorage === "undefined" || isDemoMode()) return;
  try {
    localStorage.setItem(SDCX_MACRO_LOCAL_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** 宏槽是否已被用户清除（localStorage 显式置 null） */
export function isSdcxMacroLocalCleared(macroId: number): boolean {
  const map = readLocalMap();
  return map[String(macroId)] === null;
}

/** 读取单个宏槽本地录制数据 */
export function readSdcxMacroLocalSlot(
  macroId: number
): SdcxMacroLocalSlotRecord | null {
  const map = readLocalMap();
  const record = map[String(macroId)];
  if (!record || typeof record !== "object") return null;
  if (!Array.isArray(record.actions)) return null;
  return record;
}

/** 写入单个宏槽本地录制数据 */
export function writeSdcxMacroLocalSlot(
  macroId: number,
  record: Omit<SdcxMacroLocalSlotRecord, "updatedAt">
): void {
  if (isDemoMode()) return;
  const map = readLocalMap();
  map[String(macroId)] = {
    actions: Array.isArray(record.actions) ? record.actions : [],
    valid: Boolean(record.valid),
    mode: Number(record.mode) === 1 ? 1 : 0,
    repNum: Math.max(1, Number(record.repNum) || 10),
    updatedAt: Date.now(),
  };
  writeLocalMap(map);
}

/** 清除单个宏槽本地录制数据 */
export function clearSdcxMacroLocalSlot(macroId: number): void {
  if (isDemoMode()) return;
  const map = readLocalMap();
  map[String(macroId)] = null;
  writeLocalMap(map);
}

function readIntervalPrefMap(): Record<string, SdcxMacroIntervalPrefRecord> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(SDCX_MACRO_INTERVAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeIntervalPrefMap(
  map: Record<string, SdcxMacroIntervalPrefRecord>
): void {
  if (typeof localStorage === "undefined" || isDemoMode()) return;
  try {
    localStorage.setItem(SDCX_MACRO_INTERVAL_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** 读取宏槽间隔 UI 偏好（设备 macro_tab 不存该字段） */
export function readSdcxMacroIntervalPref(macroId: number): {
  intervalMode: SdcxMacroIntervalMode;
  fixedIntervalMs: number;
} {
  const map = readIntervalPrefMap();
  const hit = map[String(macroId)];
  if (!hit) {
    return {
      intervalMode: 0,
      fixedIntervalMs: SDCX_MACRO_DEFAULT_DELAY_MS,
    };
  }
  return {
    intervalMode: hit.intervalMode === 1 ? 1 : 0,
    fixedIntervalMs: Math.max(
      1,
      Number(hit.fixedIntervalMs) || SDCX_MACRO_DEFAULT_DELAY_MS
    ),
  };
}

/** 写入宏槽间隔 UI 偏好（按 macroId 隔离） */
export function writeSdcxMacroIntervalPref(
  macroId: number,
  intervalMode: SdcxMacroIntervalMode,
  fixedIntervalMs: number
): void {
  if (isDemoMode()) return;
  const map = readIntervalPrefMap();
  map[String(macroId)] = {
    intervalMode: intervalMode === 1 ? 1 : 0,
    fixedIntervalMs: Math.max(1, Math.round(fixedIntervalMs)),
  };
  writeIntervalPrefMap(map);
}

export function clearSdcxMacroIntervalPref(macroId: number): void {
  if (isDemoMode()) return;
  const map = readIntervalPrefMap();
  delete map[String(macroId)];
  writeIntervalPrefMap(map);
}

/**
 * 解析宏槽间隔设置：优先已保存宏的本地槽位，其次间隔偏好，最后 API/列表回传
 */
export function resolveSdcxMacroIntervalForUi(
  macroId: number,
  fallback?: {
    mode?: number;
    repNum?: number;
    fixedIntervalMs?: number;
  }
): { intervalMode: SdcxMacroIntervalMode; fixedIntervalMs: number } {
  const local = readSdcxMacroLocalSlot(macroId);
  if (local?.valid && local.actions.length > 0) {
    return {
      intervalMode: local.mode === 1 ? 1 : 0,
      fixedIntervalMs: Math.max(
        1,
        Number(local.repNum) || SDCX_MACRO_DEFAULT_DELAY_MS
      ),
    };
  }

  const pref = readSdcxMacroIntervalPref(macroId);
  const map = readIntervalPrefMap();
  if (map[String(macroId)]) {
    return pref;
  }

  if (fallback) {
    return {
      intervalMode: Number(fallback.mode) === 1 ? 1 : 0,
      fixedIntervalMs: Math.max(
        1,
        Number(fallback.repNum ?? fallback.fixedIntervalMs) ||
          SDCX_MACRO_DEFAULT_DELAY_MS
      ),
    };
  }

  return pref;
}

/** 批量读取各槽摘要，供宏列表回显 */
export function readSdcxMacroLocalSummaries(): Array<{
  macroId: number;
  actNum: number;
  valid: boolean;
  mode: number;
  repNum: number;
  actions: SdcxMacroLocalAction[];
}> {
  return Array.from({ length: SDCX_MACRO_UI_SLOT_COUNT }, (_, macroId) => {
    const local = readSdcxMacroLocalSlot(macroId);
    const actions = local?.actions ?? [];
    const valid = Boolean(local?.valid && actions.length > 0);
    return {
      macroId,
      actNum: valid ? actions.length : 0,
      valid,
      mode: local?.mode ?? 0,
      repNum: local?.repNum ?? 10,
      actions: valid ? actions : [],
    };
  });
}
